import { FC, useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { ApplicationCertificate } from "../../modules/core/certificates"
import { FormSwitch, PageViewLink } from "."
import { CountriesList } from "../../resources"

import { useAuth } from "../../modules/auth"
import { KTSVG, useDebounce } from "../../../_metronic/helpers"

import { EditableDate, EditableFile, EditableInput, EditableMultiFile, EditableSelect, EditableSwitch, EditableTextarea } from "./EditableFields"
import { cloneDeep } from "lodash"

import { useEditableProductModalState } from "../dialogs/EditableProductDialog"


const DateFields = [
    "originalIssueDate",
    "issueDate",
    "latestRenewal",
    "implementationDate",
    "lastRevisionDate",
    "nextSurveillanceDate",
    "lastMajorNC",
    "expiryDate",
    "lastAuditDate",
];

type Props = {
    title : string
    details : ApplicationCertificate
    onEditDetails?: Function
}

export const CertificateDetailsCard: FC<Props> = ({title = 'CoC', details, onEditDetails}) => {

    const { currentUser } = useAuth();

    const { setOnSave, setDetails, toggleShow } = useEditableProductModalState(state => state);

    const  [update_details, setUpdateDetails ] = useState<string | undefined>();
    const [ certData, setCertData ]  = useState<ApplicationCertificate |null>(null);
        
    const debouncedUpdateDetails = useDebounce(update_details, 800);
    
    const onChangeDetail = ({ name, value, dat } : { name : string, value: string, dat?: any }) => {
        
        if (! certData) return;

        let _data = cloneDeep(certData);
        
        if (name === 'actualCoC' && dat) {
            let _coc = certData.actualCoC ? cloneDeep(certData.actualCoC) : {};
            Object.assign(_coc, { id : dat.id, fileName: dat.name});
            Object.assign(_data, { actualCoC: _coc});
        } else if (name === 'relatedDocument' && dat) {
            let _file = certData.relatedDocument ? cloneDeep(certData.relatedDocument) : {};
            Object.assign(_file, { id : dat.id, fileName: dat.name});
            Object.assign(_data, { relatedDocument: _file});
        } else {
            Object.assign(_data, { [name]: value});
        }

        setCertData(_data);

        // create update structure
        let _details = update_details !== undefined ? JSON.parse(update_details) : {};

        if (DateFields.indexOf(name) > -1) {
            // Process date fields
            const _emptyField = name + 'Empty';
            if (value.trim().length === 0) { 
                if (_details.hasOwnProperty(name)) {
                    delete _details[name];
                }
                Object.assign(_details, { [_emptyField]: true});
            } else {
                if (_details.hasOwnProperty(_emptyField)) {
                    delete _details[_emptyField];
                }
                Object.assign(_details, { [name]: value});
            }
        } else if (name === 'schemeId') {
            Object.assign(_details, { 
                productType : _data.productType.id,
                scheme : parseInt(`${value}`)
            });
        } else if (name === 'productCategory') {
            Object.assign(_details, { 
                productType : _data.productType.id,
                productCategory : parseInt(`${value}`)
            });
        } else if (name === 'relatedDocument') {
            Object.assign(_details, { relatedDocumentId: value});
        } else {            
            Object.assign(_details, { [name]: value});
        }
        setUpdateDetails(JSON.stringify(_details));
    }    
    
    const toggleProductEdit = () => {
        if (! certData) return;
        setOnSave(onEditProduct);
        setDetails({
            productType: certData.productType,
            scheme: certData.scheme,
            productCategory: certData.productCategory,
        })
        toggleShow(true);        
    }

    const onEditProduct = (values: any) => {
        if (! certData) return;

        let _hasChanges = false;
        let _data = cloneDeep(certData);
        let _details = update_details !== undefined ? JSON.parse(update_details) : {};

        const { productType, scheme, productCategory  } = _data;

        if (values?.productTypeId && values.productTypeId > 0 && productType.id !== values.productTypeId) {
            _hasChanges = true;
            Object.assign(_data, { 
                productType : {
                    ...productType, 
                    id: values.productTypeId,
                    name: values.productTypeString
                }, 
                productTypeString: values.schemeString
            });
            Object.assign(_details, { 
                productType : values.productTypeId
            });            
        }

        if (values?.schemeId && values.schemeId > 0 && scheme.id !== values.scheme) {
            _hasChanges = true;
            Object.assign(_data, { 
                scheme : {
                    ...scheme, 
                    id: values.schemeId,
                    name: values.schemeString
                }, 
                schemeString: values.schemeString
            });
            Object.assign(_details, { 
                productType: values.productTypeId,
                scheme : values.schemeId
            });            
        }

        if (values?.productCategoryId && values.productCategoryId > 0 && productCategory.id !== values.productCategoryId) {
            _hasChanges = true;
            Object.assign(_data, { 
                productCategory : {
                    ...productCategory, 
                    id: values.productCategoryId,
                    name: values.productCategoryString
                }, 
                productCategoryString: values.productCategoryString
            });
            Object.assign(_details, { 
                productType: values.productTypeId,
                productCategory : values.productCategoryId
            });            
        }

        if (! _hasChanges) return;
                
        setCertData(_data);
        setUpdateDetails(JSON.stringify(_details));
    }

    const procTempAllow = (flg: boolean) => {
        if (certData && certData?.id && onEditDetails) {
            onEditDetails({certificateid: certData.id, tempAllow: flg});
        }        
    }

    useEffect(() => {
        if (debouncedUpdateDetails === undefined || (! certData) || (! onEditDetails)) return;

        let payload = JSON.parse(debouncedUpdateDetails);
        Object.assign(payload, { certificateid: certData.id});
        onEditDetails(payload);
        setUpdateDetails(undefined);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedUpdateDetails] );

    useEffect(() => {
        if (! details) {
            setCertData(null);
            return;
        }

        let _data = cloneDeep(details);

        if (! _data?.productCategory) {
            Object.assign(_data, { productCategory : { id: 0, name: _data.productCategoryString}});
        }        

        setCertData(details);        
    }, [details]);


    if (! certData) {
        return null;
    }

    return(
    <div className="card flex-grow-1 min-w-475px" >
        <div className="card-header pt-8">
            <h3>{title} - {certData.cocNumber}</h3>
        </div>
        <div className="card-body d-flex flex-column align-items-stretch gap-2">
            <div className='row'>
                <label className='col-sm-4 col-md-3 col-lg-2 fw-bold text-muted fs-7'>Certificate Number</label>
                <EditableInput
                    containerClass="col-sm-8 col-md-3 col-lg-4"
                    fieldClass="fw-bolder fs-7 text-dark me-4"
                    name="cocNumber"
                    value={certData.cocNumber}
                    label="Certificate Number"
                    onEdit={onEditDetails ? onChangeDetail : undefined}
                >
                    &nbsp;
                    {(!  window.location.pathname.match(/view-certificate/)) &&
                    <PageViewLink id={certData.id} pageType="certificate" target="_blank">(view)</PageViewLink>
                    }                    
                </EditableInput>         
                <label className='col-sm-4 col-md-3 col-lg-2 fw-bold text-muted fs-7'>SAC Accd</label>
                <EditableSwitch
                    containerClass="col-sm-8 col-md-3 col-lg-4"
                    fieldClass="fw-bolder fs-7 text-dark me-4"
                    name="sacAcred"
                    value={certData.sacAcred ? 'YES' : 'NO'}
                    label="SAC Accd"
                    onEdit={onEditDetails ? onChangeDetail : undefined}
                />                           
            </div>
            <div className='row'>                
                <label className='col-sm-4 col-md-3 col-lg-2 fw-bold text-muted fs-7'>OEM CoC Number</label>
                <EditableInput
                    containerClass="col-sm-8 col-md-3 col-lg-4"
                    fieldClass="fw-bolder fs-7 text-dark"
                    name="parentOEM"
                    value={certData.parentOEM?.id ? `${certData.parentOEM.cocNumber}` : ''}
                    label="OEM CoC Number"
                    onEdit={onEditDetails ? onChangeDetail : undefined}
                >                 
                    &nbsp;
                    {(certData.parentOEM?.id) &&
                    <>                      
                    <PageViewLink id={certData.parentOEM.id} pageType="certificate" target="_blank">(view)</PageViewLink>
                    </>
                    }                    
                </EditableInput>                
            </div>
            <div className='row'>                
                <label className='col-sm-4 col-md-3 col-lg-2 fw-bold text-muted fs-7'>Previous CoC Number</label>
                <EditableInput
                    containerClass="col-sm-8 col-md-3 col-lg-4"
                    fieldClass="fw-bolder fs-7 text-dark"
                    name="previousCoc"
                    value={certData.previousCoc?.id ? `${certData.previousCoc.cocNumber}` : ''}
                    label="Previous CoC Number"
                    onEdit={onEditDetails ? onChangeDetail : undefined}
                >                 
                    &nbsp;
                    {(certData.previousCoc?.id) &&
                    <>                      
                    <PageViewLink id={certData.previousCoc.id} pageType="certificate" target="_blank">(view)</PageViewLink>
                    </>
                    }                    
                </EditableInput>                
            </div>
            <div className='row'>
                <label className='col-sm-4 col-md-3 col-lg-2 fw-bold text-muted fs-7'>Scheme Type</label>                
                <div className={`d-flex flex-row justify-content-start align-items-start gap-2 col-sm-8 col-md-3 col-lg-4`}>
                    <div className='fw-bolder fs-7 text-dark flex-grow-1'>{certData.schemeString}</div>
                    {(onEditDetails) &&
                    <button type="button" className="btn btn-icon btn-sm p-0 h-15px w-15px text-hover-primary" onClick={() => toggleProductEdit()} title={`Edit Scheme`}>
                        <KTSVG path='/media/icons/duotune/general/gen055.svg' className='svg-icon-0 m-0' />
                    </button>          
                    }  
                </div>                
            </div>
            <div className='row'>
                <label className='col-sm-4 col-md-3 col-lg-2 fw-bold text-muted fs-7'>Certificate Holder</label>
                <div className='col-sm-8 col-md-9 col-lg-10 fs-7'>
                    <span className='fw-bolder fs-7 text-dark'>{certData.companyName}</span>
                    &nbsp;
                    { (currentUser && currentUser.isSetsco && certData.company) &&
                        <Link to={`/setsco/view-company/${certData.company.id}`}>(view)</Link>
                    }
                </div>
            </div>
            <div className='row'>
                <label className='col-sm-4 col-md-3 col-lg-2 fw-bold text-muted fs-7'>ACRA Number</label>
                <EditableInput
                    containerClass="col-sm-8 col-md-3 col-lg-4"
                    fieldClass="fw-bolder fs-7 text-dark"
                    name="uen"
                    value={certData.uen}
                    label="ACRA Number"
                    onEdit={onEditDetails ? onChangeDetail : undefined}
                />
            </div>
            <div className='row'>
                <label className='col-sm-4 col-md-3 col-lg-2 fw-bold text-muted fs-7'>Product Type</label>                
                <div className={`d-flex flex-row justify-content-start align-items-start gap-2 col-sm-8 col-md-3 col-lg-4`}>
                    <div className='fw-bolder fs-7 text-dark flex-grow-1'>{certData.productTypeString}</div>
                    {(onEditDetails) &&
                    <button type="button" className="btn btn-icon btn-sm p-0 h-15px w-15px text-hover-primary" onClick={() => toggleProductEdit()} title={`Edit Product Type`}>
                        <KTSVG path='/media/icons/duotune/general/gen055.svg' className='svg-icon-0 m-0' />
                    </button>          
                    }  
                </div>
            </div>
            <div className='row'>
                <label className='col-sm-4 col-md-3 col-lg-2 fw-bold text-muted fs-7'>Product Category</label>                
                <div className={`d-flex flex-row justify-content-start align-items-start gap-2 col-sm-8 col-md-3 col-lg-4`}>
                    <div className='fw-bolder fs-7 text-dark flex-grow-1'>{certData?.productCategory && certData.productCategory?.name ? certData.productCategory.name : certData.productCategoryString}</div>
                    {(onEditDetails) &&
                    <button type="button" className="btn btn-icon btn-sm p-0 h-15px w-15px text-hover-primary" onClick={() => toggleProductEdit()} title={`Edit Product Category`}>
                        <KTSVG path='/media/icons/duotune/general/gen055.svg' className='svg-icon-0 m-0' />
                    </button>          
                    }             
                </div>                
            </div>
            <div className='row'>
                <label className='col-sm-4 col-md-3 col-lg-2 fw-bold text-muted fs-7'>Product</label>                
                <EditableInput
                    containerClass="col-sm-8 col-md-3 col-lg-4"
                    fieldClass="fw-bolder fs-7 text-dark"
                    name="product"
                    value={certData.product}
                    label="Product"
                    onEdit={onEditDetails ? onChangeDetail : undefined}
                />
            </div>
            <div className='row'>
                <label className='col-sm-4 col-md-3 col-lg-2 fw-bold text-muted fs-7'>Brand</label>
                <EditableInput
                    containerClass="col-sm-8 col-md-3 col-lg-4"
                    fieldClass="fw-bolder fs-7 text-dark"
                    name="productBrandString"
                    value={certData.productBrandString}
                    label="Brand"
                    onEdit={onEditDetails ? onChangeDetail : undefined}
                />
            </div>
            <div className='row'>
                <label className='col-sm-4 col-md-3 col-lg-2 fw-bold text-muted fs-7'>Model</label>
                <EditableInput
                    containerClass="col-sm-8 col-md-3 col-lg-4"
                    fieldClass="fw-bolder fs-7 text-dark"
                    name="productModelString"
                    value={certData.productModelString}
                    label="Model"
                    onEdit={onEditDetails ? onChangeDetail : undefined}
                />
            </div>
            <div className='row'>                
                <label className='col-sm-4 col-md-3 col-lg-2 fw-bold text-muted fs-7'>Country of Origin</label>
                <EditableSelect
                    containerClass="col-sm-8 col-md-3 col-lg-4"
                    fieldClass="fw-bolder fs-7 text-dark"
                    name="country"
                    value={certData.country}
                    display={certData.country}
                    label="Country of Origin"
                    selectOptions={CountriesList.map(o => ({ val: o.name, desc: o.name}))}
                    onEdit={onEditDetails ? onChangeDetail : undefined}
                />                
            </div>
            <div className='row'>
                <label className='col-sm-4 col-md-3 col-lg-2 fw-bold text-muted fs-7'>Product Details</label>
                <EditableTextarea
                    containerClass="col-sm-8 col-md-3 col-lg-4"
                    fieldClass="fw-bolder fs-7 text-dark"
                    name="productDetails"
                    value={certData.productDetails}
                    label="Product Details"
                    onEdit={onEditDetails ? onChangeDetail : undefined}
                />
            </div>
            <div className='row'>
                <label className='col-sm-4 col-md-3 col-lg-2 fw-bold text-muted fs-7'>Standards</label>
                <EditableTextarea
                    containerClass="col-sm-8 col-md-3 col-lg-4"
                    fieldClass="fw-bolder fs-7 text-dark"
                    name="standards"
                    value={certData.standards}
                    label="Standards"
                    onEdit={onEditDetails ? onChangeDetail : undefined}
                />
            </div>
            <div className='row'>
                <label className='col-sm-4 col-md-3 col-lg-2 fw-bold text-muted fs-7'>Test Reports</label>
                <EditableTextarea
                    containerClass="col-sm-8 col-md-3 col-lg-4"
                    fieldClass="fw-bolder fs-7 text-dark"
                    name="testReports"
                    value={certData.testReports}
                    label="Test Reports"
                    onEdit={onEditDetails ? onChangeDetail : undefined}
                />
            </div>
            <div className='row'>
                <label className='col-sm-4 col-md-3 col-lg-2 fw-bold text-muted fs-7'>Issue Date</label>
                <EditableDate
                    containerClass="col-sm-8 col-md-3 col-lg-4"
                    fieldClass="fw-bolder fs-7 text-dark"
                    name="issueDate"
                    value={certData.issueDate}
                    label="Issue Date"
                    onEdit={onEditDetails ? onChangeDetail : undefined}
                />
                <label className='col-sm-4 col-md-3 col-lg-2 fw-bold text-muted fs-7'>Original Issue Date</label>                
                <EditableDate
                    containerClass="col-sm-8 col-md-3 col-lg-4"
                    fieldClass="fw-bolder fs-7 text-dark"
                    name="originalIssueDate"
                    value={certData.originalIssueDate}
                    label="Original Issue Date"
                    onEdit={onEditDetails ? onChangeDetail : undefined}
                />
            </div>
            <div className='row'>
                <label className='col-sm-4 col-md-3 col-lg-2 fw-bold text-muted fs-7'>Expiration Date</label>
                <EditableDate
                    containerClass="col-sm-8 col-md-3 col-lg-4"
                    fieldClass="fw-bolder fs-7 text-dark"
                    name="expiryDate"
                    value={certData.expiryDate}
                    label="Expiry Date"
                    onEdit={onEditDetails ? onChangeDetail : undefined}
                />
                <label className='col-sm-4 col-md-3 col-lg-2 fw-bold text-muted fs-7'>Next Surveillance Date</label>
                <EditableDate
                    containerClass="col-sm-8 col-md-3 col-lg-4"
                    fieldClass="fw-bolder fs-7 text-dark"
                    name="nextSurveillanceDate"
                    value={certData.nextSurveillanceDate}
                    label="Next Surveillance Date"
                    onEdit={onEditDetails ? onChangeDetail : undefined}
                />
            </div>
            <div className='row'>
                <label className='col-sm-4 col-md-3 col-lg-2 fw-bold text-muted fs-7'>Latest Renewal</label>
                <EditableDate
                    containerClass="col-sm-8 col-md-3 col-lg-4"
                    fieldClass="fw-bolder fs-7 text-dark"
                    name="latestRenewal"
                    value={certData.latestRenewal}
                    label="Latest Renewal Date"
                    onEdit={onEditDetails ? onChangeDetail : undefined}
                />
                <label className='col-sm-4 col-md-3 col-lg-2 fw-bold text-muted fs-7'>Date of Last Revision</label>
                <EditableDate
                    containerClass="col-sm-8 col-md-3 col-lg-4"
                    fieldClass="fw-bolder fs-7 text-dark"
                    name="lastRevisionDate"
                    value={certData.lastRevisionDate}
                    label="Last Revision Date"
                    onEdit={onEditDetails ? onChangeDetail : undefined}
                />
            </div>

            <div className='row'>
                <label className='col-sm-4 col-md-3 col-lg-2 fw-bold text-muted fs-7'>Latest Major NC</label>
                <EditableDate
                    containerClass="col-sm-8 col-md-3 col-lg-4"
                    fieldClass="fw-bolder fs-7 text-dark"
                    name="lastMajorNC"
                    value={certData.lastMajorNC}
                    label="Last Major NC Date"
                    onEdit={onEditDetails ? onChangeDetail : undefined}
                />
                <label className='col-sm-4 col-md-3 col-lg-2 fw-bold text-muted fs-7'>Last Audit Date</label>
                <EditableDate
                    containerClass="col-sm-8 col-md-3 col-lg-4"
                    fieldClass="fw-bolder fs-7 text-dark"
                    name="lastAuditDate"
                    value={certData.lastAuditDate}
                    label="Last Audit Date"
                    onEdit={onEditDetails ? onChangeDetail : undefined}
                />            
            </div>

            <div className='row'>
                <label className='col-sm-4 col-md-3 col-lg-2 fw-bold text-muted fs-7'>Status</label>                
                <EditableSelect
                    containerClass="col-sm-8 col-md-3 col-lg-4"
                    fieldClass="fw-bolder fs-7 text-dark"
                    name="cocStatus"
                    value={certData.cocStatus}
                    display={certData.cocStatus}
                    label="Status"
                    selectOptions={['Active', 'Expired', 'Suspended', 'Terminated','Withdrawn'].map(o => ({ val: o, desc: o}))}
                    onEdit={onEditDetails ? onChangeDetail : undefined}
                />                                
                <label className='col-sm-4 col-md-3 col-lg-2 fw-bold text-muted fs-7'>Effective Date of Status</label>
                <EditableDate
                    containerClass="col-sm-8 col-md-3 col-lg-4"
                    fieldClass="fw-bolder fs-7 text-dark"
                    name="implementationDate"
                    value={certData.implementationDate}
                    label="Effective Date of Status"
                    onEdit={onEditDetails ? onChangeDetail : undefined}
                />
            </div>
            { certData.cocStatus === 'Suspended' &&
            <div className='row'>
                <label className='col-sm-4 col-md-3 col-lg-2 fw-bold text-muted fs-7'>Suspension Reason</label>
                <EditableTextarea
                    containerClass="col-sm-8 col-md-9 col-lg-10"
                    fieldClass="fw-bolder fs-7 text-dark"
                    name="suspensionReason"
                    value={certData.suspensionReason}
                    label="Suspension Reason"
                    onEdit={onEditDetails ? onChangeDetail : undefined}
                />
            </div>
            }            
            <div className='row'>
                <label className='col-sm-4 col-md-3 col-lg-2 fw-bold text-muted fs-7'>Certificate</label>
                <EditableFile
                    containerClass="col-sm-8 col-md-3 col-lg-4"
                    fieldClass="fw-bolder fs-7 text-dark"
                    name="actualCoC"
                    fileData={certData.actualCoC}
                    label="Certificate of Confirmation"
                    onEdit={onEditDetails ? onChangeDetail : undefined}
                    downloadable={true}
                />                
                <label className='col-sm-4 col-md-3 col-lg-2 fw-bold text-muted fs-7'>Related Document</label>
                <EditableMultiFile
                    containerClass="col-sm-8 col-md-3 col-lg-4"
                    fieldClass="fw-bolder fs-7 text-dark"
                    name="relatedDocument"
                    fileData={certData.relatedDocument}
                    label="Related Document"
                    onEdit={onEditDetails ? onChangeDetail : undefined}
                    downloadable={true}
                />                  
            </div>
            { (certData.parentApplication && certData.parentApplication.id) &&
            <div className='row'>
                <label className='col-sm-4 col-md-3 col-lg-2 fw-bold text-muted fs-7'>Initial Application</label>
                <div className='col-sm-8 col-md-9 col-lg-10'>
                    <span className="fw-bolder fs-7 text-dark">{certData.parentApplication.id}</span>
                    &nbsp;
                    {(!  window.location.pathname.match(/view-application/)) &&
                    <PageViewLink id={certData.parentApplication.id} pageType="application" target="_blank">(view)</PageViewLink>
                    }
                </div>
            </div>
            }
            { (window.location.pathname.match(/view-certificate/) && currentUser && currentUser.isSetsco) && 
            <div className='row my-2'>
                <label className='col-sm-4 col-md-3 col-lg-2 fw-bold text-muted fs-7'>Temporarily Allow DOC/ROI Application</label>
                <div className='col-sm-8 col-md-9 col-lg-10'>
                    <FormSwitch name="tempAllow" value={certData.tempAllow} onChange={procTempAllow} />
                </div>
            </div>
            }
        </div>        
    </div>

    )
}